import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LogOut,
  Users,
  UserPlus,
  MessageSquare,
  MessagesSquare,
  Phone,
  Building2,
  Mail,
  Search,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, clearToken } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import AdminUsersPanel from "@/components/AdminUsersPanel";
import BackupPanel from "@/components/BackupPanel";
import SiteSettingsPanel from "@/components/SiteSettingsPanel";

function StatCard({ icon: Icon, label, value, testId, accent }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-slate-200 rounded-2xl p-5"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl ${accent} flex items-center justify-center`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
          {label}
        </div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

const STATUS_STYLES = {
  new: "bg-red-50 text-red-700 border-red-200",
  contacted: "bg-amber-50 text-amber-700 border-amber-200",
  qualified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

function LeadRow({ lead, onStatusChange, onView }) {
  return (
    <tr
      data-testid={ADMIN.leadRow}
      className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors"
    >
      <td className="px-4 py-4">
        <div className="font-semibold text-slate-900 text-sm">{lead.name}</div>
        <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
          <Building2 className="w-3 h-3" /> {lead.company}
        </div>
      </td>
      <td className="px-4 py-4">
        <div className="text-sm text-slate-700 flex items-center gap-1.5">
          <Phone className="w-3.5 h-3.5 text-slate-400" /> {lead.phone}
        </div>
        {lead.email && (
          <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
            <Mail className="w-3 h-3" /> {lead.email}
          </div>
        )}
      </td>
      <td className="px-4 py-4 text-sm text-slate-700 max-w-xs">
        <div className="line-clamp-2">{lead.needs}</div>
      </td>
      <td className="px-4 py-4">
        <select
          value={lead.status}
          onChange={(e) => onStatusChange(lead.id, e.target.value)}
          className={`text-xs font-medium border rounded-full px-2.5 py-1 focus:outline-none focus:ring-2 focus:ring-red-500 ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}
        >
          <option value="new">Baru</option>
          <option value="contacted">Dihubungi</option>
          <option value="qualified">Qualified</option>
          <option value="closed">Selesai</option>
        </select>
      </td>
      <td className="px-4 py-4 text-xs text-slate-500 whitespace-nowrap">
        {new Date(lead.created_at).toLocaleString("id-ID", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </td>
      <td className="px-4 py-4">
        <button
          onClick={() => onView(lead.session_id)}
          disabled={!lead.session_id}
          className="text-xs text-[#DC2626] hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
        >
          Lihat Chat
        </button>
      </td>
    </tr>
  );
}

function ConversationViewer({ sessionId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) return;
    setLoading(true);
    api.get(`/admin/conversations/${sessionId}`)
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => toast.error("Gagal memuat percakapan"))
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (!sessionId) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-500 h-full flex flex-col items-center justify-center">
        <MessagesSquare className="w-10 h-10 text-slate-300 mb-3" />
        Pilih lead atau percakapan untuk melihat isi chat
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col h-[600px]">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div>
          <div className="text-xs text-slate-500">Percakapan</div>
          <div className="font-mono text-xs text-slate-700 truncate max-w-[250px]">
            {sessionId}
          </div>
        </div>
        <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-900">
          Tutup
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 chat-scroll">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-sm text-slate-500 py-10">Belum ada pesan</div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap rounded-2xl ${
                  m.role === "user"
                    ? "bg-[#DC2626] text-white rounded-tr-sm"
                    : "bg-slate-100 text-slate-900 rounded-tl-sm"
                }`}
              >
                {m.content.replace(/\[SHOW_LEAD_FORM\]/g, "")}
                <div className={`text-[10px] mt-1 ${m.role === "user" ? "text-white/70" : "text-slate-400"}`}>
                  {new Date(m.created_at).toLocaleTimeString("id-ID", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("leads");

  const load = async () => {
    try {
      const [meRes, s, l, c] = await Promise.all([
        api.get("/admin/me"),
        api.get("/admin/stats"),
        api.get("/admin/leads"),
        api.get("/admin/conversations"),
      ]);
      setMe(meRes.data.user);
      setStats(s.data);
      setLeads(l.data.leads || []);
      setConversations(c.data.conversations || []);
    } catch (e) {
      if (e.response?.status === 401) {
        clearToken();
        nav("/admin");
      } else {
        toast.error("Gagal memuat data");
      }
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    clearToken();
    nav("/admin");
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}/status`, { status });
      toast.success("Status diperbarui");
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      toast.error("Gagal update status");
    }
  };

  const filtered = leads.filter((l) => {
    const q = search.toLowerCase();
    return (
      !q ||
      l.name.toLowerCase().includes(q) ||
      l.company.toLowerCase().includes(q) ||
      l.phone.toLowerCase().includes(q) ||
      l.needs.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div>
              <div className="font-display font-bold text-[15px] leading-none tracking-tight">
                Admin Dashboard
              </div>
              <div className="text-[10px] text-slate-500 leading-none mt-1">
                Chatbot Widget
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end mr-1 min-w-[160px]">
              {me ? (
                <>
                  <div className="text-xs font-semibold text-slate-900 leading-tight">{me.name}</div>
                  <div className="text-[10px] text-slate-500 leading-tight mt-0.5">
                    {me.role === "super_admin" ? "Super Admin" : "Admin"} • {me.email}
                  </div>
                </>
              ) : (
                <>
                  <div className="h-3 w-24 bg-slate-100 rounded animate-pulse" />
                  <div className="h-2.5 w-32 bg-slate-100 rounded animate-pulse mt-1.5" />
                </>
              )}
            </div>
            <button
              data-testid={ADMIN.logoutBtn}
              onClick={logout}
              className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors font-medium"
            >
              <LogOut className="w-4 h-4" /> Keluar
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <StatCard
            icon={Users}
            label="Total Leads"
            value={stats?.total_leads ?? "—"}
            testId={ADMIN.statTotalLeads}
            accent="bg-red-50 text-[#DC2626]"
          />
          <StatCard
            icon={UserPlus}
            label="Leads Baru"
            value={stats?.new_leads ?? "—"}
            testId={ADMIN.statNewLeads}
            accent="bg-emerald-50 text-emerald-600"
          />
          <StatCard
            icon={MessagesSquare}
            label="Percakapan"
            value={stats?.total_conversations ?? "—"}
            testId={ADMIN.statConversations}
            accent="bg-indigo-50 text-indigo-600"
          />
          <StatCard
            icon={MessageSquare}
            label="Total Pesan"
            value={stats?.total_messages ?? "—"}
            testId={ADMIN.statMessages}
            accent="bg-amber-50 text-amber-600"
          />
        </motion.div>

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
          {[
            { id: "leads", label: "Leads", count: leads.length },
            { id: "conversations", label: "Percakapan", count: conversations.length },
            { id: "users", label: "Kelola Admin", count: null },
            ...(me?.role === "super_admin"
              ? [
                  { id: "settings", label: "Pengaturan", count: null },
                  { id: "backup", label: "Backup", count: null },
                ]
              : []),
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium relative transition-colors whitespace-nowrap ${
                tab === t.id ? "text-[#DC2626]" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {t.label}
              {t.count !== null && (
                <span className="ml-2 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                  {t.count}
                </span>
              )}
              {tab === t.id && (
                <motion.div
                  layoutId="tab-indicator"
                  className="absolute -bottom-px left-0 right-0 h-[2px] bg-[#DC2626]"
                />
              )}
            </button>
          ))}
        </div>

        {/* Content grid */}
        {tab === "users" ? (
          <div className="mt-6">
            <AdminUsersPanel currentUser={me} />
          </div>
        ) : tab === "settings" ? (
          <div className="mt-6">
            <SiteSettingsPanel currentUser={me} />
          </div>
        ) : tab === "backup" ? (
          <div className="mt-6">
            <BackupPanel currentUser={me} />
          </div>
        ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            {tab === "leads" ? (
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Cari leads..."
                      className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div className="text-xs text-slate-500">
                    {filtered.length} dari {leads.length} leads
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table data-testid={ADMIN.leadsTable} className="w-full">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50/50">
                        <th className="px-4 py-3 font-medium">Kontak</th>
                        <th className="px-4 py-3 font-medium">Telepon / Email</th>
                        <th className="px-4 py-3 font-medium">Kebutuhan</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium">Waktu</th>
                        <th className="px-4 py-3 font-medium">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                            {leads.length === 0
                              ? "Belum ada lead masuk. Leads akan muncul otomatis saat pengunjung mengisi form di chatbot."
                              : "Tidak ada lead sesuai pencarian."}
                          </td>
                        </tr>
                      ) : (
                        filtered.map((l) => (
                          <LeadRow
                            key={l.id}
                            lead={l}
                            onStatusChange={changeStatus}
                            onView={setSelectedSession}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div
                data-testid={ADMIN.conversationsList}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-medium">
                  Percakapan Terbaru
                </div>
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto chat-scroll">
                  {conversations.length === 0 ? (
                    <div className="px-4 py-12 text-center text-sm text-slate-500">
                      Belum ada percakapan.
                    </div>
                  ) : (
                    conversations.map((c) => (
                      <button
                        key={c.session_id}
                        data-testid={ADMIN.conversationItem}
                        onClick={() => setSelectedSession(c.session_id)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${
                          selectedSession === c.session_id ? "bg-red-50" : ""
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-mono text-xs text-slate-700 truncate max-w-[200px]">
                            {c.session_id}
                          </div>
                          {c.has_lead && (
                            <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded font-medium">
                              LEAD
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                          <span>{c.message_count} pesan</span>
                          <span>
                            {new Date(c.last_message_at).toLocaleString("id-ID", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <ConversationViewer
              sessionId={selectedSession}
              onClose={() => setSelectedSession(null)}
            />
          </div>
        </div>
        )}
      </main>
    </div>
  );
}
